import React, { useCallback, useRef } from "react";
import { CitationExpansionContext } from "./citation-expansion-context";

type ExpansionHandler = (citationNumber: number) => void;

/**
 * Provider for managing citation expansion across message components.
 * This replaces the window event system with a more efficient context-based approach.
 *
 * Instead of N event listeners (one per MessageSources component), we have a single
 * centralized registry that directly calls the appropriate handler.
 */
export function CitationExpansionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Map of messageId -> expansion handler function
  const handlersRef = useRef<Map<string, ExpansionHandler>>(new Map());

  /**
   * Register an expansion handler for a specific message.
   * Returns cleanup function to unregister when component unmounts.
   */
  const registerExpansionHandler = useCallback(
    (messageId: string, handler: ExpansionHandler) => {
      handlersRef.current.set(messageId, handler);

      // Return cleanup function
      return () => {
        handlersRef.current.delete(messageId);
      };
    },
    [],
  );

  /**
   * Trigger expansion for a specific message's citations.
   * Directly calls the registered handler instead of broadcasting to all listeners.
   */
  const expandCitation = useCallback(
    (messageId: string, citationNumber: number) => {
      const handler = handlersRef.current.get(messageId);
      if (handler) {
        handler(citationNumber);
      }
    },
    [],
  );

  return (
    <CitationExpansionContext.Provider
      value={{ registerExpansionHandler, expandCitation }}
    >
      {children}
    </CitationExpansionContext.Provider>
  );
}
